import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";
import { toast } from "sonner";

interface AccountPreferences {
  pinnedTabs?: string[];
  [key: string]: unknown;
}

export function usePinnedTabs() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const queryKey = ["account_preferences", user?.id];

  const { data: preferences, isLoading } = useQuery<AccountPreferences>({
    queryKey,
    queryFn: async () => {
      if (!user) return {};
      const { data, error } = await supabase
        .from("accounts")
        .select("preferences")
        .eq("user_id", user.id)
        .maybeSingle();
      if (error) throw error;
      return (data?.preferences as AccountPreferences) ?? {};
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const pinnedKeys: string[] = Array.isArray(preferences?.pinnedTabs) ? preferences.pinnedTabs : [];

  const togglePin = async (key: string) => {
    if (!user) return;

    const current = pinnedKeys;
    const isPinned = current.includes(key);
    const next = isPinned
      ? current.filter((k) => k !== key)   // unpin: remove, keep order of rest
      : [...current, key];                 // pin: append at end

    // Optimistic update
    const previousPrefs = queryClient.getQueryData<AccountPreferences>(queryKey);
    const optimisticPrefs: AccountPreferences = {
      ...previousPrefs,
      pinnedTabs: next,
    };
    queryClient.setQueryData(queryKey, optimisticPrefs);

    // Persist to Supabase
    const { error } = await supabase
      .from("accounts")
      .update({ preferences: optimisticPrefs as unknown as Json })
      .eq("user_id", user.id);

    if (error) {
      // Rollback
      queryClient.setQueryData(queryKey, previousPrefs);
      toast.error("Couldn't update pinned tabs.");
    }
  };

  return { pinnedKeys, togglePin, isLoading };
}
